import { HeaderText, Separator } from "@/components/ui";

import {
  bodyTemperatureFormSchema,
  bpFormSchema,
  headCircumferenceFormSchema,
  heartRateFormSchema,
  heightFormSchema,
  weightFormSchema,
} from "./schemas/schemas";
import { formatHeight, parseHeightString } from "./utils/heightUtils";
import { formatWeight, parseWeightString } from "./utils/weightUtils";

import { usePatientDemographics } from "@/features/Patient/PatientBar/network/usePatientDemographics";
import { useParams } from "@tanstack/react-router";
import { BmiVital } from "./components/BmiVital";
import { VitalItem } from "./components/VitalItem";
import {
  type BodyTemperatureObservationParams,
  type ComponentDisplayData,
  type HeadCircumferenceObservationParams,
  type HeartRateObservationParams,
  type HeightObservationParams,
  type ValueUnitDisplayData,
  VitalsEnum,
  type WeightObservationParams,
} from "./config/vitalsTypes";
export const Vitals = () => {
  const { patientId } = useParams({ strict: false });

  const { data: patientDemographics } = usePatientDemographics(
    patientId as string,
  );

  return (
    <section
      className="w-full h-full flex flex-col"
      aria-label="Patient vitals"
      tabIndex={-1}
    >
      <HeaderText className="pb-3" size="md">
        Vitals
      </HeaderText>
      <Separator />
      <fieldset
        className="flex flex-col sm:flex-row gap-2 border-0 m-0 p-0"
        aria-label="Height and weight measurements"
      >
        <div className="w-full sm:w-1/2">
          <VitalItem
            type={VitalsEnum.HEIGHT}
            title="Height"
            formSchema={heightFormSchema}
            formDescription={'Enter as "6ft 2in", "74in", or "1m 80cm"'}
            format={(data: ValueUnitDisplayData, fallback: string) => {
              if (data.value && data.unit) {
                return (
                  formatHeight(
                    data.value,
                    data.unit as HeightObservationParams["unit"],
                  ) || fallback
                );
              }
              if (data.value) {
                return String(data.value);
              }
              return fallback;
            }}
            createMutationPayload={(
              patientId: string,
              formValue: string,
              reportedDate: string,
            ): HeightObservationParams => {
              const { value, unit } = parseHeightString(formValue);
              return {
                patient_id: patientId,
                height: value,
                unit: unit as HeightObservationParams["unit"],
                reported_date: reportedDate,
              };
            }}
          />
        </div>
        <div className="w-full sm:w-1/2">
          <VitalItem
            type={VitalsEnum.WEIGHT}
            title="Weight"
            formSchema={weightFormSchema}
            formDescription="Enter as 200 lbs or 75 kg"
            format={(data: ValueUnitDisplayData, fallback: string) => {
              if (data.value && data.unit) {
                return (
                  formatWeight(
                    data.value,
                    data.unit as WeightObservationParams["unit"],
                  ) || fallback
                );
              }
              if (data.value) {
                return String(data.value);
              }
              return fallback;
            }}
            createMutationPayload={(
              patientId: string,
              formValue: string,
              reportedDate: string,
            ): WeightObservationParams => {
              const { value, unit } = parseWeightString(formValue);
              return {
                patient_id: patientId,
                weight: value,
                unit: unit as WeightObservationParams["unit"],
                reported_date: reportedDate,
              };
            }}
          />
        </div>
      </fieldset>
      <Separator />
      <fieldset
        className="flex flex-col sm:flex-row gap-2 border-0 m-0 p-0"
        aria-label="BMI and blood pressure measurements"
      >
        <div className="w-full sm:w-1/2">
          <BmiVital />
        </div>
        <div className="w-full sm:w-1/2">
          <VitalItem
            type={VitalsEnum.BLOOD_PRESSURE}
            title="BP"
            formSchema={bpFormSchema}
            formDescription="Enter as 120/80"
            format={(data: ComponentDisplayData, fallback: string) => {
              if (data.values?.length === 2) {
                return `${data.values[0]} / ${data.values[1]}` || fallback;
              }
              return fallback;
            }}
            createMutationPayload={(
              patientId: string,
              formValue: string,
              reportedDate: string,
            ) => {
              const [systolic, diastolic] = formValue.split("/");
              return {
                patient_id: patientId,
                systolic_pressure: Number(systolic),
                diastolic_pressure: Number(diastolic),
                reported_date: reportedDate,
              };
            }}
          />
        </div>
      </fieldset>
      <Separator />
      <fieldset
        className="flex flex-col sm:flex-row gap-2 border-0 m-0 p-0"
        aria-label="Heart rate and body temperature measurements"
      >
        <div className="w-full sm:w-1/2">
          <VitalItem
            type={VitalsEnum.HEART_RATE}
            title="Heart Rate"
            formSchema={heartRateFormSchema}
            formDescription="Enter as '100' (beats per minute)"
            format={(data: ValueUnitDisplayData, fallback: string) =>
              data.value ? `${data.value} BPM` : fallback
            }
            getFormValue={(data: ValueUnitDisplayData) =>
              data.value ? String(data.value) : ""
            }
            createMutationPayload={(
              patientId: string,
              formValue: string,
              reportedDate: string,
            ): HeartRateObservationParams => ({
              patient_id: patientId,
              heart_rate: Number(formValue),
              reported_date: reportedDate,
            })}
          />
        </div>
        <div className="w-full sm:w-1/2">
          <VitalItem
            type={VitalsEnum.BODY_TEMPERATURE}
            title="Temperature"
            formSchema={bodyTemperatureFormSchema}
            formDescription="Enter as '98.6' (degrees Fahrenheit)"
            format={(data: ValueUnitDisplayData, fallback: string) =>
              data.value ? `${data.value}ºF` : fallback
            }
            getFormValue={(data: ValueUnitDisplayData) =>
              data.value ? String(data.value) : ""
            }
            createMutationPayload={(
              patientId: string,
              formValue: string,
              reportedDate: string,
            ): BodyTemperatureObservationParams => ({
              patient_id: patientId,
              body_temperature: Number(formValue),
              reported_date: reportedDate,
            })}
          />
        </div>
      </fieldset>
      {Number(patientDemographics?.demographics?.age) <= 3 ? (
        <>
          <Separator />
          <fieldset
            className="flex flex-col sm:flex-row gap-2 border-0 m-0 p-0"
            aria-label="Head circumference measurement"
          >
            <div className="w-1/2">
              <VitalItem
                type={VitalsEnum.HEAD_CIRCUMFERENCE}
                title="Head Circumference"
                formSchema={headCircumferenceFormSchema}
                formDescription="Enter as '45 cm'"
                format={(data: ValueUnitDisplayData, fallback: string) =>
                  data.value ? `${data.value} cm` : fallback
                }
                getFormValue={(data: ValueUnitDisplayData) =>
                  data.value ? String(data.value) : ""
                }
                createMutationPayload={(
                  patientId: string,
                  formValue: string,
                  reportedDate: string,
                ): HeadCircumferenceObservationParams => ({
                  patient_id: patientId,
                  head_circumference: Number(formValue.replace("cm", "")),
                  reported_date: reportedDate,
                })}
              />
            </div>
          </fieldset>
        </>
      ) : null}
    </section>
  );
};
